import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "Why engineers are worth so much",
  "description": "Engineers don't do the day to day work, they build assets the business can exploit for years to come. That's the magic",
  "published": "2020-07-24T00:00:00.000Z",
  "redirect_from": ["/blog/why-engineers-are-worth-so-much/swizec/9458"],
  "categories": "Opinions, Personal, SeniorMindset",
  "content_upgrade": "SeniorMindset",
  "hero": "./img/techletter.app-screenshot-1595609659436.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`Your boss comes up to you and says `}<em parentName="p">{`"Yo we've got the perfect project for you"`}</em></p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/3o7527pa7qs9kCG78A/giphy-loop.mp4?cid=7cf28b0fjo7o0f9ig9vcmwpaoovq6wooslyklnsr4ot3m0cu&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p><em parentName="p">{`"We need new functionality in this feature and you're the last person to work on it. Means you're the best fit`}</em></p>
    <p>{`Didn't I work on this 3 years ago?`}</p>
    <p><em parentName="p">{`"Yes, hasn't been touched since"`}</em></p>
    <p>{`And that's why engineers are worth the big bux my friend. `}<strong parentName="p">{`We build assets.`}</strong></p>
    <ContentUpgrades.SeniorMindset />
    <h2 {...{
      "id": "how-engineers-build-assets"
    }}>{`How engineers build assets`}</h2>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Another huge mindset shift was 👇`}<br parentName="p"></br><br parentName="p"></br>{`We grow up thinking that effort is what you’re paid for. Nobody gives a shit how hard you work, mate. `}<br parentName="p"></br><br parentName="p"></br>{`They care how much value you bring.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1285989900110577664"
      }}>{`July 22, 2020`}</a></blockquote>
    <p>{`Consider a typical job: You do the work, create value, get paid.`}</p>
    <p>{`Service jobs are like that.`}</p>
    <p>{`You go to the DMV, see the bored bureaucrat and you try to lighten their day. Smile and a chitchat. Doesn't work. Still grumpy.`}</p>
    <p>{`Now imagine it from their end.`}</p>
    <p>{`You're sitting in your chair all day having the same damn conversations. These fools come up to you, all saying the same stupid chitchatty words ... yes you got in a wreck, no this never happens to you otherwise, yes it was the other person's fault, yep the lines are long, yeah it's wednesday, mhm weather sure is nice ... 🤮`}</p>
    <p>{`You grab their papers, add the stamp, count there's 3, say the same old `}<em parentName="p">{`"Okay take these papers to window 5, tell them Margarite sent you, ask for article 2a-dash-c, I added a postit note. Then you go to ...`}</em></p>
    <p>{`Every day. Every week. Every month. For years.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/fXK6nNrqoW9NNWWcPD/giphy-loop.mp4?cid=7cf28b0f7m88vjcoji2d8f1vx9aapqm9ex80m9r359rvd6fj&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`You'd be grumpy too.`}</p>
    <p>{`Compare that to an engineering job.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`The important work is the work you haven’t done before. `}<br parentName="p"></br><br parentName="p"></br>{`We’re engineers not line cooks.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1283475726432264192"
      }}>{`July 15, 2020`}</a></blockquote>
    <p>{`You get a project. You iterate with the product team, the design team, the other engineers.`}</p>
    <p>{`It's a long and sometimes painful process. You bang your head against the wall. Problem after problem. Issues always come up.`}</p>
    <p>{`You ship to production and wish you never have to look at this bullshit ever again.`}</p>
    <p><strong parentName="p">{`And unlike the bureaucrat, you don't!!`}</strong>{` You're done.`}</p>
    <p>{`Tomorrow brings a new challenge. A new project. New idea. New everything. You're using the same technology, `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/path-dependence/swizec/9448"
      }}>{`building on your past projects`}</a>{`, sure, `}<em parentName="p">{`but you're building something new.`}</em></p>
    <p>{`That's because you've created an asset.`}</p>
    <p>{`An asset that's going to keep doing `}<del parentName="p">{`its`}</del>{` your job long after you've moved on. After you've left the company even. Your code's gonna keep chugging along making cash.`}</p>
    <p><strong parentName="p">{`That's`}</strong>{` why engineers are worth 💰.`}</p>
    <p>{`And lest you think we're alone: Designers build assets when they create design systems, marketers build assets with funnels and email chains, managers build assets with improved processes, entrepreneurs build assets with teams.`}</p>
    <p>{`The trick is to do work that works when you don't.`}</p>
    <h2 {...{
      "id": "use-this-concept-to-up-your-salary"
    }}>{`Use this concept to up your salary`}</h2>
    <p>{`Your value is in building assets. Now what?`}</p>
    <p>{`Use that knowledge to increase your salary. In my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/5-years-of-books-courses-or-how-i-made-369000-on-the-side/swizec/9453"
      }}>{`5 years of books & courses or how I made `}{`\\`}{`$369,000 on the side`}</a>{` article, I mention between the lines that day job income went from $60,000 to $175,000 in 5 years.`}</p>
    <p>{`That's a 3x difference. 😇`}</p>
    <p>{`How?`}</p>
    <p>{`Part of it is leverage. Having a sidehustle means I can negotiate aggressively. Savings mean I can always walk away.`}</p>
    <p>{`The `}<strong parentName="p">{`big part is understanding your value`}</strong>{` and finding folks who value it. That's the trick.`}</p>
    <h2 {...{
      "id": "your-work-is-worth-different--to-different-people"
    }}>{`Your work is worth different 💰 to different people`}</h2>
    <p>{`You can do N hours of coding per day. When you're done you're done. You can't juice a squeezed lemon.`}</p>
    <p>{`Who should you sell those value-creating hours to?`}</p>
    <p>{`Let's say you create a landing page. Standard work, nothing hard. Direct revenue generating asset.`}</p>
    <p>{`2000 visitors, 200 clicks, 20 purchases. 1% conversion, not bad.`}</p>
    <p>{`Company A sells a $9 product. Company B sells a $90 product.`}</p>
    <p>{`Your 5 hours of work are worth $180 to Company A and $1800 to Company B. Same 5 hours of work, same difficulty of effort.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/64aBc1uYI7asXZH28O/giphy-loop.mp4?cid=7cf28b0fv2be9km1kdgs1x9a52bodtepp06w1luhcyhj0v1z&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Company C has a huge brand, massive traffic machinery, and a $90 product. 2,000,000 visitors land on your new page on day 1.`}</p>
    <p>{`👉 you created a $1,800,000 asset. Same 5 hours of easy effort.`}</p>
    <p>{`🤯`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: this is why SFBA has these crazy salary dynamics. There's more unicorns per capita than anywhere else in the world.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      